import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { format, subDays } from 'date-fns';
import logo from '../assets/logo.png';
import {
  INTRO_DIALOG_LABEL,
  INTRO_TAGLINE,
  introCardsFor,
  type IntroCard,
} from './dashboardIntroCopy';
import MainLayout from '../components/MainLayout';
import { PageHeader } from '../components/ui/PageHeader';
import { Dropdown } from '../components/ui/Dropdown';
import { CallInsightsStats } from '../components/adherence/types';
import MetricCard, { TopItem } from '../components/dashboard/MetricCard';
import ScoreTrendsSection from '../components/dashboard/ScoreTrendsSection';
import { ScoreTrends, ScoreTrendsPayload } from '../components/dashboard/trendTypes';
import { filterScoreTrends } from '../components/dashboard/filterScoreTrends';
import { CARD_ACCENTS } from '../config/dashboardTheme';
import { apiService } from '../services/apiService';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useTutorial } from '../hooks/useTutorial';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { setMongoUser } from '../store/authSlice';
import { areLoginGatesCleared } from '../utils/loginGates';
import { isExcludedAnalyticsEmail } from '../utils/excludedAnalyticsEmails';
import NewInquiriesModal from '../components/NewInquiriesModal';
import CommonObjectionsModal from '../components/CommonObjectionsModal';
import CommonFAQsModal from '../components/CommonFAQsModal';
import CommonLandminesModal from '../components/CommonLandminesModal';
import CommonCompetitorsModal from '../components/CommonCompetitorsModal';
import HardestItemsModal from '../components/dashboard/HardestItemsModal';

interface AnalyticsData {
  totalSessions: number;
  totalDuration: number;
  avgDuration: number;
  totalTranscripts: number;
  totalObjections: number;
  totalFAQs: number;
  totalNewInquiries: number;
  totalUnprocessedInquiries: number;
  totalLandmineDetections: number;
  totalCompetitorMentions: number;
  uniqueUserCount: number;
}

/** Two most frequent items per category, fetched alongside the headline counts. */
interface CardPreviews {
  objections: TopItem[];
  faqs: TopItem[];
  newInquiries: TopItem[];
  landmines: TopItem[];
  competitors: TopItem[];
}

interface UserInfo {
  userId: string;
  userEmail: string;
  name?: string;
}

interface Session {
  _id: string;
  sessionId: string;
  sessionName: string;
  userEmail: string;
  callStartTime: string;
  callDuration: number;
  statistics: {
    totalObjections: number;
    totalFAQs: number;
    totalNewInquiries: number;
    totalLandmineDetections?: number;
    totalCompetitorMentions?: number;
  };
}

interface InquiryData {
  sessionId: string;
  sessionName: string;
  userEmail: string;
  userId: string;
  callStartTime: string;
  inquiry: {
    question: string;
    answer?: string;
    category: string;
    timestamp: string;
    relevance_score: number;
    isProcessed: boolean;
  };
}

interface ObjectionDetection {
  question: string;
  sessionId: string;
}

interface CommonObjectionData {
  groupId: string;
  sourceId: string | null;
  parentId: string | null;
  originalQuestion: string | null;
  originalAnswer: string | null;
  detections: ObjectionDetection[];
  totalCount: number;
  category: string;
  storedAnswer: string | null;
}

interface FAQDetection {
  question: string;
  sessionId: string;
}

interface CommonFAQData {
  groupId: string;
  sourceId: string | null;
  parentId: string | null;
  originalQuestion: string | null;
  originalAnswer: string | null;
  detections: FAQDetection[];
  totalCount: number;
  category: string;
  storedAnswer: string | null;
}

interface LandmineDetection {
  matchedText: string;
  sessionId: string;
  speaker?: string;
}

interface CommonLandmineData {
  sourceId: string | null;
  trigger: string;
  triggerVariations: string[];
  detections: LandmineDetection[];
  totalCount: number;
  category: string;
  priority: 'high' | 'medium' | 'low';
}

interface CompetitorDetection {
  matchedText: string;
  sessionId: string;
  speaker?: string;
}

interface CommonCompetitorData {
  sourceId: string | null;
  name: string;
  variations: string[];
  detections: CompetitorDetection[];
  totalCount: number;
  category: string;
  avgConfidence: number;
}

type TimeFilter = '7days' | '30days' | 'all';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin } = useRoleAccess();
  const { mongoUser, tutorialActiveStepIndex, termsStatus } = useAppSelector((state) => state.auth);
  const { isTutorialCompleted, startTutorial } = useTutorial();
  const dispatch = useAppDispatch();
  const [introStep, setIntroStep] = useState(0);
  const [introOpen, setIntroOpen] = useState(false);
  // Two shared cards plus the role-specific next step. The COUNT drives the
  // dots and the Next/Finish switch, so adding a card needs no other edit.
  const introCards: IntroCard[] = useMemo(() => introCardsFor(isAdmin), [isAdmin]);
  // Clamped read. `introStep` is only bounded downward, and the list is
  // rebuilt from `isAdmin`; if that ever flips after the user advances, an
  // unguarded index returns undefined and the render throws on `.title`.
  const activeCard = introCards[Math.min(introStep, introCards.length - 1)];

  // State
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('30days');
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [recentSessions, setRecentSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New Inquiries Modal State
  const [showInquiriesModal, setShowInquiriesModal] = useState(false);
  const [inquiries, setInquiries] = useState<InquiryData[]>([]);
  const [loadingInquiries, setLoadingInquiries] = useState(false);

  // Common Objections Modal State
  const [showObjectionsModal, setShowObjectionsModal] = useState(false);
  const [commonObjections, setCommonObjections] = useState<CommonObjectionData[]>([]);
  const [loadingObjections, setLoadingObjections] = useState(false);

  // Common FAQs Modal State
  const [showFAQsModal, setShowFAQsModal] = useState(false);
  const [commonFAQs, setCommonFAQs] = useState<CommonFAQData[]>([]);
  const [loadingFAQs, setLoadingFAQs] = useState(false);

  // Common Landmines Modal State
  const [showLandminesModal, setShowLandminesModal] = useState(false);
  const [commonLandmines, setCommonLandmines] = useState<CommonLandmineData[]>([]);
  const [loadingLandmines, setLoadingLandmines] = useState(false);

  // Common Competitors Modal State
  const [showCompetitorsModal, setShowCompetitorsModal] = useState(false);
  const [commonCompetitors, setCommonCompetitors] = useState<CommonCompetitorData[]>([]);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);

  // Hardest Items Modals State
  const [showHardestObjectionsModal, setShowHardestObjectionsModal] = useState(false);
  const [showHardestFAQsModal, setShowHardestFAQsModal] = useState(false);
  const [showHardestLandminesModal, setShowHardestLandminesModal] = useState(false);
  const [showHardestCompetitorsModal, setShowHardestCompetitorsModal] = useState(false);

  // Backs the "Hardest X" drill-down modals
  const [callInsightsStats, setCallInsightsStats] = useState<CallInsightsStats | null>(null);

  // Card previews — own endpoint so a slow preview never delays the counts
  const [cardPreviews, setCardPreviews] = useState<CardPreviews | null>(null);

  // Scoring trend charts
  const [scoreTrends, setScoreTrends] = useState<ScoreTrends | null>(null);
  const [loadingTrends, setLoadingTrends] = useState(true);
  const [comparedRepIds, setComparedRepIds] = useState<string[]>([]);

  const isOrgWide = isAdmin && selectedUserId === 'all';

  useEffect(() => {
    fetchAnalytics();
    fetchCardPreviews();
    fetchRecentSessions();
    fetchCallInsightsStats();
    if (isAdmin) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, selectedUserId, isAdmin]);

  // Rep overlays only apply org-wide; drop them when scoping to one person.
  useEffect(() => {
    if (!isOrgWide && comparedRepIds.length > 0) {
      setComparedRepIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrgWide]);

  useEffect(() => {
    fetchScoreTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, selectedUserId, isAdmin, comparedRepIds]);

  // Orientation and the Shepherd tour are deliberately independent. The
  // former is a brief one-time explanation; the latter remains the complete
  // interface tour and owns its own completion preference.
  useEffect(() => {
    if (mongoUser && !isTutorialCompleted && !mongoUser.preferences?.firstLoginIntroductionCompleted && tutorialActiveStepIndex === null && areLoginGatesCleared(termsStatus)) setIntroOpen(true);
  }, [mongoUser, isTutorialCompleted, tutorialActiveStepIndex, termsStatus]);

  const finishIntroduction = async (showTour = false) => {
    setIntroOpen(false);
    setIntroStep(0);
    // Preserve both the old tour state and a user's choice to skip it. The
    // auth endpoint merges preferences, so this cannot reset the tour.
    if (mongoUser && !mongoUser.preferences?.firstLoginIntroductionCompleted) {
      try {
        await apiService.put('/auth/profile', { preferences: { firstLoginIntroductionCompleted: true } });
        dispatch(setMongoUser({
          ...mongoUser,
          preferences: {
            theme: mongoUser.preferences?.theme || 'light',
            notifications: mongoUser.preferences?.notifications || { email: true, push: true },
            tutorialCompleted: mongoUser.preferences?.tutorialCompleted,
            firstLoginIntroductionCompleted: true,
          },
        }));
      } catch (error) {
        console.error('Failed to save first-login introduction preference:', error);
      }
    }
    if (showTour) startTutorial();
  };

  const getDateRange = () => {
    const now = new Date();
    let dateFrom: string | undefined;

    switch (timeFilter) {
      case '7days':
        dateFrom = subDays(now, 7).toISOString();
        break;
      case '30days':
        dateFrom = subDays(now, 30).toISOString();
        break;
      case 'all':
      default:
        dateFrom = undefined;
    }

    return { dateFrom, dateTo: now.toISOString() };
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/analytics', params);

      if (response.success && response.data) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchCallInsightsStats = async () => {
    try {
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/call-insights-quality', params);
      if (response.success && response.data) {
        setCallInsightsStats(response.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch call insights stats:', err);
    }
  };

  // Failure here leaves the cards without their preview lines but never blocks
  // the headline counts, which come from a separate request.
  const fetchCardPreviews = async () => {
    try {
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/card-previews', params);
      if (response.success && response.data) {
        setCardPreviews(response.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch card previews:', err);
    }
  };

  const fetchScoreTrends = async () => {
    try {
      setLoadingTrends(true);

      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }
      if (comparedRepIds.length > 0) {
        params.repIds = comparedRepIds.join(',');
      }

      const response = await apiService.get('/admin/statistics/score-trends', params);
      if (response.success && response.data) {
        setScoreTrends(filterScoreTrends(response.data as ScoreTrendsPayload));
      }
    } catch (err: any) {
      console.error('Failed to fetch score trends:', err);
    } finally {
      setLoadingTrends(false);
    }
  };

  // Populate the selector from ALL org users (any role, active or not) — not
  // just those who happen to have sessions — since admins also run sales calls.
  const fetchUsers = async () => {
    try {
      const response: any = await apiService.get('/admin/users', { limit: '500' });
      const list: any[] = response?.data?.users ?? response?.data ?? [];
      const mapped: UserInfo[] = list
        .filter((u) => u && (u.firebaseUid || u.uid) && u.email && !isExcludedAnalyticsEmail(u.email))
        .map((u) => ({
          userId: u.firebaseUid || u.uid,
          userEmail: u.email,
          name: u.name,
        }))
        .sort((a, b) =>
          (a.name || a.userEmail).localeCompare(b.name || b.userEmail, undefined, {
            sensitivity: 'base',
          })
        );
      setUsers(mapped);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchRecentSessions = async () => {
    try {
      const { dateFrom, dateTo } = getDateRange();
      const params = new URLSearchParams({
        page: '1',
        limit: '5',
        sortBy: 'callStartTime',
        sortOrder: 'desc',
      });

      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      if (isAdmin && selectedUserId !== 'all') {
        params.append('userId', selectedUserId);
      }

      const response = await apiService.get(`/admin/sessions?${params}`);

      if (response.success && response.data) {
        // Normalize before filtering: an object envelope (or anything other
        // than a bare array) would otherwise throw on .filter and be
        // swallowed by the catch below.
        const sessions: Session[] = Array.isArray(response.data) ? response.data : [];
        setRecentSessions(sessions.filter((s) => !isExcludedAnalyticsEmail(s.userEmail)));
      }
    } catch (err: any) {
      console.error('Failed to fetch recent sessions:', err);
    }
  };

  const fetchInquiries = async () => {
    try {
      setLoadingInquiries(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/inquiries/new', params);

      if (response.success && response.data) {
        setInquiries(response.data.inquiries || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch inquiries:', err);
      setError('Failed to load inquiries');
    } finally {
      setLoadingInquiries(false);
    }
  };

  const handleNewInquiriesClick = async () => {
    setShowInquiriesModal(true);
    await fetchInquiries();
  };

  const fetchCommonObjections = async () => {
    try {
      setLoadingObjections(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/common-objections', params);

      if (response.success && response.data) {
        setCommonObjections(response.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch common objections:', err);
    } finally {
      setLoadingObjections(false);
    }
  };

  const fetchCommonFAQs = async () => {
    try {
      setLoadingFAQs(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/common-faqs', params);

      if (response.success && response.data) {
        setCommonFAQs(response.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch common FAQs:', err);
    } finally {
      setLoadingFAQs(false);
    }
  };

  const handleObjectionsClick = async () => {
    setShowObjectionsModal(true);
    await fetchCommonObjections();
  };

  const handleFAQsClick = async () => {
    setShowFAQsModal(true);
    await fetchCommonFAQs();
  };

  const fetchCommonLandmines = async () => {
    try {
      setLoadingLandmines(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/common-landmines', params);

      if (response.success && response.data) {
        setCommonLandmines(response.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch common landmines:', err);
    } finally {
      setLoadingLandmines(false);
    }
  };

  const fetchCommonCompetitors = async () => {
    try {
      setLoadingCompetitors(true);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== 'all') {
        params.userId = selectedUserId;
      }

      const response = await apiService.get('/admin/statistics/common-competitors', params);

      if (response.success && response.data) {
        setCommonCompetitors(response.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch common competitors:', err);
    } finally {
      setLoadingCompetitors(false);
    }
  };

  const handleLandminesClick = async () => {
    setShowLandminesModal(true);
    await fetchCommonLandmines();
  };

  const handleCompetitorsClick = async () => {
    setShowCompetitorsModal(true);
    await fetchCommonCompetitors();
  };

  const handleInquiryProcessed = () => {
    // Refresh analytics to update the count
    fetchAnalytics();
    // Refresh inquiries list
    fetchInquiries();
  };

  const handleTimeFilterChange = (filter: TimeFilter) => {
    setTimeFilter(filter);
  };

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const viewSessionDetails = (sessionId: string) => {
    navigate(`/sessions/${sessionId}`);
  };

  const viewAllSessions = () => {
    navigate('/sessions');
  };

  // "Hardest X" drill-downs need the scored insights, and are admin-only.
  const canShowHardest = callInsightsStats != null && isAdmin;

  const metricCards = analytics ? [
    {
      title: 'Total Sessions',
      value: analytics.totalSessions,
      accentColor: CARD_ACCENTS.sessions,
    },
    {
      title: 'Objections',
      value: analytics.totalObjections,
      accentColor: CARD_ACCENTS.objections,
      topItems: cardPreviews?.objections,
      primaryAction: { label: 'View all', onClick: handleObjectionsClick },
      secondaryAction: canShowHardest
        ? { label: 'Hardest', onClick: () => setShowHardestObjectionsModal(true) }
        : undefined,
    },
    {
      title: 'FAQs',
      value: analytics.totalFAQs,
      accentColor: CARD_ACCENTS.faqs,
      topItems: cardPreviews?.faqs,
      primaryAction: { label: 'View all', onClick: handleFAQsClick },
      secondaryAction: canShowHardest
        ? { label: 'Hardest', onClick: () => setShowHardestFAQsModal(true) }
        : undefined,
    },
    {
      title: 'New Objection / FAQ',
      value: analytics.totalNewInquiries,
      accentColor: CARD_ACCENTS.newInquiries,
      topItems: cardPreviews?.newInquiries,
      caption: `${analytics.totalUnprocessedInquiries} unprocessed`,
      primaryAction: { label: 'View all', onClick: handleNewInquiriesClick },
    },
    {
      title: 'Landmines',
      value: analytics.totalLandmineDetections,
      accentColor: CARD_ACCENTS.landmines,
      topItems: cardPreviews?.landmines,
      primaryAction: { label: 'View all', onClick: handleLandminesClick },
      secondaryAction: canShowHardest
        ? { label: 'Hardest', onClick: () => setShowHardestLandminesModal(true) }
        : undefined,
    },
    {
      title: 'Competitors',
      value: analytics.totalCompetitorMentions,
      accentColor: CARD_ACCENTS.competitors,
      topItems: cardPreviews?.competitors,
      primaryAction: { label: 'View all', onClick: handleCompetitorsClick },
      secondaryAction: canShowHardest
        ? { label: 'Hardest', onClick: () => setShowHardestCompetitorsModal(true) }
        : undefined,
    },
  ] : [];

  // Super admins have no access to dashboard — redirect them to knowledge base
  if (isSuperAdmin) {
    return <Navigate to="/knowledge-base" replace />;
  }

  return (
    <MainLayout title="Dashboard">
      <div className="p-2">
        {/* Header + filters share one row so the page opens on data, not chrome */}
        <PageHeader
          title="Analytics"
          description={isAdmin
            ? selectedUserId === 'all'
              ? 'Across the organization'
              : users.find((u) => u?.userId === selectedUserId)?.userEmail || 'Selected user'
            : 'Your personal overview'}
          actions={(
            <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={timeFilter}
              onChange={(_, next) => next && handleTimeFilterChange(next as TimeFilter)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.5 } }}
            >
              <ToggleButton value="7days">7 days</ToggleButton>
              <ToggleButton value="30days">30 days</ToggleButton>
              <ToggleButton value="all">All time</ToggleButton>
            </ToggleButtonGroup>

            {isAdmin && (
              <Dropdown
                label="View analytics for"
                width="wide"
                value={selectedUserId}
                onChange={handleUserChange}
                options={[
                  { value: 'all', label: 'Across organization' },
                  ...users.map((user) => ({
                    value: user.userId,
                    label: user.name ? `${user.name} (${user.userEmail})` : user.userEmail,
                  })),
                ]}
              />
            )}
            </>
          )}
        />

        {/* Error Display */}
        {error && (
          <Alert severity="error" className="mb-6">
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && !analytics ? (
          <div className="flex justify-center items-center py-12">
            <CircularProgress />
          </div>
        ) : analytics ? (
          <div className="relative">
            {/* Loading Overlay - shows when refetching data */}
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center rounded-lg">
                <div className="flex flex-col items-center">
                  <CircularProgress />
                  <Typography variant="body2" color="textSecondary" className="mt-2">
                    Updating analytics...
                  </Typography>
                </div>
              </div>
            )}

            {/* Summary cards — headline count plus the two most frequent items */}
            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              {metricCards.map((card) => (
                <MetricCard
                  key={card.title}
                  title={card.title}
                  value={card.value}
                  accentColor={card.accentColor}
                  topItems={card.topItems}
                  caption={card.caption}
                  primaryAction={card.primaryAction}
                  secondaryAction={card.secondaryAction}
                />
              ))}
            </div>

            <ScoreTrendsSection
              trends={scoreTrends}
              loading={loadingTrends}
              isOrgWide={isOrgWide}
              selectedRepIds={comparedRepIds}
              onSelectedRepsChange={setComparedRepIds}
            />

            {/* Session statistics — one compact strip rather than its own card */}
            <div className="mb-6 grid grid-cols-2 gap-3 rounded-lg border border-hairline bg-surface p-4 sm:grid-cols-4">
              {[
                { label: 'Total duration', value: formatDuration(analytics.totalDuration) },
                { label: 'Average duration', value: formatDuration(analytics.avgDuration) },
                { label: 'Transcripts', value: analytics.totalTranscripts?.toLocaleString() ?? '0' },
                ...(isOrgWide
                  ? [{ label: 'Active users', value: String(analytics.uniqueUserCount ?? 0) }]
                  : []),
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-sm font-semibold uppercase tracking-wide text-ink">{stat.label}</p>
                  <p className="mt-0.5 text-base font-semibold text-ink">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Recent Sessions */}
            <section className="rounded-lg border border-hairline bg-surface p-5">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="heading-4 font-semibold">Recent sessions</h2>
                <button
                  type="button"
                  onClick={viewAllSessions}
                  className="text-xs font-medium text-ink-secondary underline-offset-2 hover:text-ink hover:underline"
                >
                  View all sessions
                </button>
              </div>

              {recentSessions.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-muted">
                  No sessions in the selected period
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-hairline">
                        {['Session', 'User', 'Date', 'Duration', 'Objections', 'FAQs', 'New'].map(
                          (heading, index) => (
                            <th
                              key={heading}
                              className={`pb-2 text-xs font-medium uppercase tracking-wide text-ink-muted ${
                                index > 3 ? 'text-right' : 'text-left'
                              } ${index === 0 ? 'pr-4' : 'px-4'}`}
                            >
                              {heading}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-hairline">
                      {recentSessions.map((session) => (
                        <tr
                          key={session._id}
                          onClick={() => viewSessionDetails(session.sessionId)}
                          className="cursor-pointer transition-colors hover:bg-surface-subtle"
                        >
                          <td className="py-2.5 pr-4 text-sm font-medium text-ink">
                            {session.sessionName}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-ink-muted">{session.userEmail}</td>
                          <td className="px-4 py-2.5 text-sm text-ink-secondary">
                            {format(new Date(session.callStartTime), 'MMM d, yyyy HH:mm')}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-ink-secondary">
                            {formatDuration(session.callDuration)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-sm tabular-nums text-ink-secondary">
                            {session.statistics?.totalObjections ?? 0}
                          </td>
                          <td className="px-4 py-2.5 text-right text-sm tabular-nums text-ink-secondary">
                            {session.statistics?.totalFAQs ?? 0}
                          </td>
                          <td className="px-4 py-2.5 text-right text-sm tabular-nums text-ink-secondary">
                            {session.statistics?.totalNewInquiries ?? 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        ) : null}

        {/* New Inquiries Modal */}
        <NewInquiriesModal
          isOpen={showInquiriesModal}
          onClose={() => setShowInquiriesModal(false)}
          inquiries={inquiries}
          loading={loadingInquiries}
          onInquiryProcessed={handleInquiryProcessed}
        />

        {/* Common Objections Modal */}
        <CommonObjectionsModal
          isOpen={showObjectionsModal}
          onClose={() => setShowObjectionsModal(false)}
          objections={commonObjections}
          loading={loadingObjections}
        />

        {/* Common FAQs Modal */}
        <CommonFAQsModal
          isOpen={showFAQsModal}
          onClose={() => setShowFAQsModal(false)}
          faqs={commonFAQs}
          loading={loadingFAQs}
        />

        {/* Common Landmines Modal */}
        <CommonLandminesModal
          isOpen={showLandminesModal}
          onClose={() => setShowLandminesModal(false)}
          landmines={commonLandmines}
          loading={loadingLandmines}
        />

        {/* Common Competitors Modal */}
        <CommonCompetitorsModal
          isOpen={showCompetitorsModal}
          onClose={() => setShowCompetitorsModal(false)}
          competitors={commonCompetitors}
          loading={loadingCompetitors}
        />

        {/* Hardest Objections Modal */}
        {callInsightsStats && (
          <HardestItemsModal
            isOpen={showHardestObjectionsModal}
            onClose={() => setShowHardestObjectionsModal(false)}
            title="Hardest Objections"
            columnHeader="Objection"
            items={callInsightsStats.weakest_objections}
            itemType="objection_faq"
            dateFrom={getDateRange().dateFrom}
            dateTo={getDateRange().dateTo}
            userId={isAdmin && selectedUserId !== 'all' ? selectedUserId : undefined}
          />
        )}

        {/* Hardest FAQs Modal */}
        {callInsightsStats && (
          <HardestItemsModal
            isOpen={showHardestFAQsModal}
            onClose={() => setShowHardestFAQsModal(false)}
            title="Hardest FAQs"
            columnHeader="FAQ"
            items={callInsightsStats.weakest_faqs}
            itemType="objection_faq"
            dateFrom={getDateRange().dateFrom}
            dateTo={getDateRange().dateTo}
            userId={isAdmin && selectedUserId !== 'all' ? selectedUserId : undefined}
          />
        )}

        {/* Hardest Landmines Modal */}
        {callInsightsStats && (
          <HardestItemsModal
            isOpen={showHardestLandminesModal}
            onClose={() => setShowHardestLandminesModal(false)}
            title="Hardest Landmines"
            columnHeader="Landmine"
            items={callInsightsStats.hardest_landmines}
            itemType="landmine"
            dateFrom={getDateRange().dateFrom}
            dateTo={getDateRange().dateTo}
            userId={isAdmin && selectedUserId !== 'all' ? selectedUserId : undefined}
          />
        )}

        {/* Hardest Competitors Modal */}
        {callInsightsStats && (
          <HardestItemsModal
            isOpen={showHardestCompetitorsModal}
            onClose={() => setShowHardestCompetitorsModal(false)}
            title="Hardest Competitors"
            columnHeader="Competitor"
            items={callInsightsStats.hardest_competitors}
            itemType="competitor"
            dateFrom={getDateRange().dateFrom}
            dateTo={getDateRange().dateTo}
            userId={isAdmin && selectedUserId !== 'all' ? selectedUserId : undefined}
          />
        )}
        <Dialog
          open={introOpen}
          onClose={() => finishIntroduction(false)}
          aria-labelledby="first-login-introduction-title"
          aria-describedby="first-login-introduction-body"
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 'var(--radius-card)', backgroundColor: 'var(--surface)' } }}
        >
          {/* The dialog's accessible name is deliberately the product name and
              NOT the visible headline. It has to stay stable while the three
              cards change beneath it, and DashboardTutorialGating asserts on
              it to test gating rather than copy. Visually hidden, not absent:
              a dialog with no name is a screen-reader dead end. */}
          <DialogTitle
            id="first-login-introduction-title"
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {INTRO_DIALOG_LABEL}
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 3, sm: 6 }, pt: 6, pb: 2, textAlign: 'center' }}>
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{ width: 132, height: 'auto', display: 'block', mx: 'auto', mb: 3 }}
            />
            <Typography
              component="p"
              sx={{
                fontSize: 'var(--text-h3)',
                lineHeight: 'var(--lh-h3)',
                fontWeight: 'var(--fw-medium)',
                color: 'var(--ink)',
                maxWidth: '30ch',
                mx: 'auto',
                mb: 5,
              }}
            >
              {INTRO_TAGLINE}
            </Typography>

            <Typography
              component="h2"
              sx={{
                fontSize: 'var(--text-h4)',
                lineHeight: 'var(--lh-h4)',
                fontWeight: 'var(--fw-semibold)',
                color: 'var(--ink)',
                mb: 1.5,
              }}
            >
              {activeCard.title}
            </Typography>
            <Typography
              id="first-login-introduction-body"
              sx={{
                fontSize: 'var(--text-body-l)',
                lineHeight: 'var(--lh-body-l)',
                color: 'var(--ink-secondary)',
                maxWidth: '52ch',
                mx: 'auto',
                minHeight: '4.8em',
              }}
            >
              {activeCard.body}
            </Typography>

            {/* Position, not decoration. The dots carry it visually and are
                hidden from assistive tech, so the same fact is given as text
                beside them — otherwise the one thing these exist to convey is
                the one thing a screen-reader user does not get. */}
            <Box
              sx={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {`Step ${introStep + 1} of ${introCards.length}`}
            </Box>
            {/* The literal dimensions here are deliberate: dot sizes, the logo
                width and the transition are bespoke to this component, and
                tokens.css carries no motion scale and no dimension token that
                means "a progress dot". The COLOURS and radii are tokens, which
                is what has to survive a re-theme. Line height above is likewise
                literal because only --lh-h1..h4 were carried into the product
                tokens; adding the body line heights has to land in the
                frontend's copy of tokens.css in the same change. */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }} aria-hidden="true">
              {introCards.map((card, index) => (
                <Box
                  key={card.title}
                  sx={{
                    width: index === introStep ? 20 : 8,
                    height: 8,
                    borderRadius: 'var(--radius-pill)',
                    backgroundColor: index === introStep ? 'var(--signature)' : 'var(--hairline)',
                    transition: 'width 0.2s, background-color 0.2s',
                  }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 3, sm: 6 }, pb: 4, pt: 2, justifyContent: 'space-between' }}>
            <Button onClick={() => finishIntroduction(false)}>Skip for now</Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setIntroStep((step) => Math.max(0, step - 1))} disabled={introStep === 0}>Back</Button>
              {introStep < introCards.length - 1
                ? <Button variant="contained" onClick={() => setIntroStep((step) => step + 1)}>Next</Button>
                : <Button variant="contained" onClick={() => finishIntroduction(true)}>Show me around</Button>}
            </Box>
          </DialogActions>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
